import React, { useRef } from 'react';
import { Download, FileText, Calendar, TrendingUp, Clock, DollarSign, MessageSquare, Sparkles } from 'lucide-react';

/**
 * ShareholderReport - Printable PDF-ready report view
 * Uses browser print functionality for clean PDF export
 */
const ShareholderReport = ({
    analytics,
    engagementScore,
    chatbotName = 'Chatbot',
    dateRange = 'Last 30 Days',
    onClose
}) => {
    const reportRef = useRef(null);

    const handlePrint = () => {
        window.print();
    };

    const conversations = analytics?.messageVolume?.totalConversations || 0;
    const avgMessages = analytics?.conversationMetrics?.avgMessagesPerConversation || 0;
    const openTickets = analytics?.ticketMetrics?.openTickets || 0;
    const resolvedTickets = analytics?.ticketMetrics?.resolvedTickets || 0;
    const totalTickets = openTickets + resolvedTickets;
    const containmentRate = totalTickets > 0 ? Math.round((resolvedTickets / totalTickets) * 100) : 0;
    const hoursSaved = Math.round(resolvedTickets * 5 / 60); // 5 min per resolved
    const costSavings = hoursSaved * 30; // $30/hr assumed

    const topics = analytics?.topTopics || [];
    // dailyActivity used for potential future activity summary
    const dailyActivity = analytics?.dailyActivity || []; // eslint-disable-line no-unused-vars

    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 print:p-0 print:bg-white">
            <div
                ref={reportRef}
                className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto print:max-w-none print:max-h-none print:rounded-none print:overflow-visible"
            >
                {/* Print Controls - Hidden in Print */}
                <div className="p-4 border-b border-slate-200 flex items-center justify-between print:hidden">
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <FileText size={18} className="text-primary" />
                        Performance Report
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                        >
                            <Download size={14} />
                            Export PDF
                        </button>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm"
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>

                {/* Report Content */}
                <div className="p-8 print:p-12">
                    {/* Header */}
                    <div className="text-center mb-8 pb-6 border-b border-slate-200">
                        <h1 className="text-2xl font-bold text-slate-800 mb-2">
                            {chatbotName} Performance Report
                        </h1>
                        <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {dateRange}
                            </span>
                            <span>Generated: {currentDate}</span>
                        </div>
                    </div>

                    {/* Executive Summary */}
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
                            <Sparkles size={16} className="text-amber-500" />
                            Executive Summary
                        </h2>
                        <div className="bg-slate-50 rounded-xl p-6">
                            <p className="text-slate-700 leading-relaxed">
                                During this period, your AI chatbot handled <strong>{conversations.toLocaleString()} conversations</strong> with
                                an average of <strong>{avgMessages.toFixed(1)} messages</strong> per conversation.
                                The self-service rate was <strong>{containmentRate}%</strong>,
                                saving approximately <strong>{hoursSaved} support hours</strong> (estimated value: <strong>${costSavings.toLocaleString()}</strong>).
                            </p>
                        </div>
                    </section>

                    {/* Key Metrics */}
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-700 mb-4">Key Metrics</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-4 bg-white border border-slate-200 rounded-lg text-center">
                                <MessageSquare size={20} className="text-violet-500 mx-auto mb-2" />
                                <div className="text-2xl font-bold text-slate-800">{conversations.toLocaleString()}</div>
                                <div className="text-xs text-slate-500">Conversations</div>
                            </div>
                            <div className="p-4 bg-white border border-slate-200 rounded-lg text-center">
                                <TrendingUp size={20} className="text-emerald-500 mx-auto mb-2" />
                                <div className="text-2xl font-bold text-slate-800">{containmentRate}%</div>
                                <div className="text-xs text-slate-500">Self-Service Rate</div>
                            </div>
                            <div className="p-4 bg-white border border-slate-200 rounded-lg text-center">
                                <Clock size={20} className="text-blue-500 mx-auto mb-2" />
                                <div className="text-2xl font-bold text-slate-800">{hoursSaved}h</div>
                                <div className="text-xs text-slate-500">Hours Saved</div>
                            </div>
                            <div className="p-4 bg-white border border-slate-200 rounded-lg text-center">
                                <DollarSign size={20} className="text-green-500 mx-auto mb-2" />
                                <div className="text-2xl font-bold text-slate-800">${costSavings.toLocaleString()}</div>
                                <div className="text-xs text-slate-500">Est. Savings</div>
                            </div>
                        </div>
                    </section>

                    {/* Top Topics */}
                    {topics.length > 0 && (
                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-slate-700 mb-4">Most Common Inquiries</h2>
                            <div className="space-y-3">
                                {topics.slice(0, 5).map((topic, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <span className="w-6 h-6 rounded bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">
                                            {index + 1}
                                        </span>
                                        <span className="flex-1 text-sm text-slate-700">{topic.phrase}</span>
                                        <span className="text-sm font-medium text-slate-500">{topic.count} queries</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Engagement Score */}
                    {engagementScore && (
                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-slate-700 mb-4">Engagement Score</h2>
                            <div className="flex items-center gap-6">
                                <div className="w-24 h-24 rounded-full border-8 border-violet-500 flex items-center justify-center">
                                    <span className="text-2xl font-bold text-slate-800">{engagementScore}</span>
                                </div>
                                <p className="flex-1 text-sm text-slate-600">
                                    Your chatbot's daily engagement score reflects its overall performance across
                                    self-service rate, efficiency, coverage, and queue health.
                                    A score of 70+ indicates excellent performance.
                                </p>
                            </div>
                        </section>
                    )}

                    {/* Footer */}
                    <footer className="pt-6 mt-8 border-t border-slate-200 text-center text-xs text-slate-400">
                        <p>Report generated by SmushLabs Analytics • {currentDate}</p>
                    </footer>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .print\\:p-12, .print\\:p-12 * {
                        visibility: visible;
                    }
                    @page {
                        margin: 0.75in;
                        size: A4;
                    }
                }
            `}</style>
        </div>
    );
};

export default ShareholderReport;
